// Field schema definitions for every import item type.
// Used by ImportItemEditor to render the appropriate form controls.
//
// Each field schema describes a single JSON field on an import item.
// The `key` matches the parser.go struct json tags exactly.

// --- Types ---

export type FieldType =
  | 'text'
  | 'textarea'
  | 'number'
  | 'select'
  | 'token_ref'
  | 'token_ref_array'
  | 'string_array'
  | 'date'
  | 'scoring' // special: nested RiskScoring object

export interface FieldSchema {
  /** JSON field name matching parser.go struct tags */
  key: string
  /** Display label */
  label: string
  /** Input type */
  type: FieldType
  /** Whether the field is required (validated server-side, shown as hint) */
  required: boolean
  /** Optional placeholder / hint text */
  placeholder?: string
  /** For 'select': the available options */
  options?: SelectOption[]
  /**
   * For 'token_ref': section paths whose tokens are valid values.
   * For 'token_ref_array': same — each element is a token from these sections.
   */
  tokenRefSections?: string[]
  /**
   * For token_ref fields that include 'lookups' in tokenRefSections:
   * only include lookups whose 'type' field matches one of these values.
   * If omitted, all lookups from the section are included.
   */
  lookupTypeFilter?: string[]
  /** If true, this field is read-only in the editor (e.g. token) */
  readOnly?: boolean
}

export interface SelectOption {
  value: string
  label: string
}

// --- Shared option sets ---

const LOOKUP_TYPE_OPTIONS: SelectOption[] = [
  { value: 'register', label: 'Register' },
  { value: 'control_category', label: 'Control Category' },
  { value: 'event_category', label: 'Event Category' },
  { value: 'action_category', label: 'Action Category' },
  { value: 'framework_category', label: 'Framework Category' },
  { value: 'taxonomy', label: 'Taxonomy' },
  { value: 'impact_table', label: 'Impact Table' },
  { value: 'ownership_role', label: 'Ownership Role' },
  { value: 'event_type', label: 'Event Type' },
  { value: 'user', label: 'User' },
]

const PERMISSION_DOMAIN_OPTIONS: SelectOption[] = [
  { value: 'register', label: 'Register' },
  { value: 'control_category', label: 'Control Category' },
  { value: 'event_category', label: 'Event Category' },
  { value: 'action_category', label: 'Action Category' },
  { value: 'framework_category', label: 'Framework Category' },
]

const PERMISSION_LEVEL_OPTIONS: SelectOption[] = [
  { value: 'read', label: 'Read' },
  { value: 'write', label: 'Write' },
]

const RISK_STATE_OPTIONS: SelectOption[] = [
  { value: 'draft', label: 'Draft' },
  { value: 'identified', label: 'Identified' },
  { value: 'assessed', label: 'Assessed' },
  { value: 'managed', label: 'Managed' },
  { value: 'closed', label: 'Closed' },
]

const CONTROL_STATE_OPTIONS: SelectOption[] = [
  { value: 'draft', label: 'Draft' },
  { value: 'active', label: 'Active' },
  { value: 'retired', label: 'Retired' },
]

const EVENT_STATE_OPTIONS: SelectOption[] = [
  { value: 'draft', label: 'Draft' },
  { value: 'active', label: 'Active' },
]

const ACTION_STATE_OPTIONS: SelectOption[] = [
  { value: 'draft', label: 'Draft' },
  { value: 'open', label: 'Open' },
  { value: 'closed', label: 'Closed' },
]

const ASSESSMENT_TYPE_OPTIONS: SelectOption[] = [
  { value: 'self_assessed_design', label: 'Self-Assessed Design' },
  { value: 'self_assessed_operating', label: 'Self-Assessed Operating' },
  { value: 'independent_design', label: 'Independent Design' },
  { value: 'independent_operating', label: 'Independent Operating' },
]

const EFFECTIVENESS_OPTIONS: SelectOption[] = [
  { value: 'effective', label: 'Effective' },
  { value: 'partially_effective', label: 'Partially Effective' },
  { value: 'ineffective', label: 'Ineffective' },
]

export const COVERAGE_LEVEL_OPTIONS: SelectOption[] = [
  { value: 'full', label: 'Full' },
  { value: 'partial', label: 'Partial' },
  { value: 'planned', label: 'Planned' },
  { value: 'not_assessed', label: 'Not Assessed' },
]

// --- All token-producing section paths (used for broad token_ref lookups) ---

/** Sections that produce user tokens */
const USER_TOKEN_SECTIONS = ['lookups', 'structure/users']
const USER_LOOKUP_FILTER = ['user']

/** Sections that produce ownership role tokens */
const OWNERSHIP_ROLE_SECTIONS = ['lookups', 'structure/ownership_roles']
const OWNERSHIP_ROLE_LOOKUP_FILTER = ['ownership_role']

/** Sections that produce register tokens */
const REGISTER_SECTIONS = ['lookups', 'structure/registers']
const REGISTER_LOOKUP_FILTER = ['register']

/** Sections that produce control category tokens */
const CONTROL_CATEGORY_SECTIONS = ['lookups', 'structure/control_categories']
const CONTROL_CATEGORY_LOOKUP_FILTER = ['control_category']

/** Sections that produce event category tokens */
const EVENT_CATEGORY_SECTIONS = ['lookups', 'structure/event_categories']
const EVENT_CATEGORY_LOOKUP_FILTER = ['event_category']

/** Sections that produce action category tokens */
const ACTION_CATEGORY_SECTIONS = ['lookups', 'structure/action_categories']
const ACTION_CATEGORY_LOOKUP_FILTER = ['action_category']

/** Sections that produce framework category tokens */
const FRAMEWORK_CATEGORY_SECTIONS = ['lookups', 'structure/framework_categories']
const FRAMEWORK_CATEGORY_LOOKUP_FILTER = ['framework_category']

/** Sections that produce event type tokens */
const EVENT_TYPE_SECTIONS = ['lookups', 'structure/event_types']
const EVENT_TYPE_LOOKUP_FILTER = ['event_type']

/** Sections that produce taxonomy tokens */
const TAXONOMY_SECTIONS = ['lookups']
const TAXONOMY_LOOKUP_FILTER = ['taxonomy']

/** Sections that produce permission role tokens */
const PERMISSION_ROLE_SECTIONS = ['permissions/roles']

/** Sections that produce risk tokens */
const RISK_SECTIONS = ['objects/risks']

/** Sections that produce control tokens */
const CONTROL_SECTIONS = ['objects/controls']

/** Sections that produce action tokens */
const ACTION_SECTIONS = ['objects/actions']

/**
 * All sections that produce node tokens for permission grants.
 * Grants can reference registers or any of the 4 category types.
 */
const GRANT_NODE_SECTIONS = [
  ...REGISTER_SECTIONS,
  ...CONTROL_CATEGORY_SECTIONS,
  ...EVENT_CATEGORY_SECTIONS,
  ...ACTION_CATEGORY_SECTIONS,
  ...FRAMEWORK_CATEGORY_SECTIONS,
]

// --- Field schemas by item type ---

export const FIELD_SCHEMAS: Record<string, FieldSchema[]> = {
  lookup: [
    { key: 'token', label: 'Token', type: 'text', required: true, readOnly: true },
    { key: 'type', label: 'Type', type: 'select', required: true, options: LOOKUP_TYPE_OPTIONS },
    { key: 'path', label: 'Path', type: 'string_array', required: false, placeholder: 'Hierarchical name chain (e.g. ["Parent", "Child"])' },
    { key: 'name', label: 'Name', type: 'text', required: false, placeholder: 'For name-based lookups (impact_table, ownership_role, event_type)' },
    { key: 'email', label: 'Email', type: 'text', required: false, placeholder: 'For user lookups' },
  ],

  user: [
    { key: 'token', label: 'Token', type: 'text', required: true, readOnly: true },
    { key: 'email', label: 'Email', type: 'text', required: true, placeholder: 'user@example.com' },
    { key: 'display_name', label: 'Display Name', type: 'text', required: true },
  ],

  ownership_role: [
    { key: 'token', label: 'Token', type: 'text', required: true, readOnly: true },
    { key: 'name', label: 'Name', type: 'text', required: true },
    { key: 'owner', label: 'Owner', type: 'token_ref', required: true, tokenRefSections: USER_TOKEN_SECTIONS, lookupTypeFilter: USER_LOOKUP_FILTER },
  ],

  register: [
    { key: 'token', label: 'Token', type: 'text', required: true, readOnly: true },
    { key: 'parent', label: 'Parent', type: 'token_ref', required: true, tokenRefSections: REGISTER_SECTIONS, lookupTypeFilter: REGISTER_LOOKUP_FILTER },
    { key: 'name', label: 'Name', type: 'text', required: true },
    { key: 'description', label: 'Description', type: 'textarea', required: false },
    { key: 'owner', label: 'Owner', type: 'token_ref', required: true, tokenRefSections: OWNERSHIP_ROLE_SECTIONS, lookupTypeFilter: OWNERSHIP_ROLE_LOOKUP_FILTER },
  ],

  category: [
    { key: 'token', label: 'Token', type: 'text', required: true, readOnly: true },
    { key: 'parent', label: 'Parent', type: 'token_ref', required: true, tokenRefSections: [] }, // resolved dynamically per category type
    { key: 'name', label: 'Name', type: 'text', required: true },
    { key: 'description', label: 'Description', type: 'textarea', required: false },
    { key: 'owner', label: 'Owner', type: 'token_ref', required: true, tokenRefSections: OWNERSHIP_ROLE_SECTIONS, lookupTypeFilter: OWNERSHIP_ROLE_LOOKUP_FILTER },
  ],

  event_type: [
    { key: 'token', label: 'Token', type: 'text', required: true, readOnly: true },
    { key: 'name', label: 'Name', type: 'text', required: true },
  ],

  permission_role: [
    { key: 'token', label: 'Token', type: 'text', required: true, readOnly: true },
    { key: 'name', label: 'Name', type: 'text', required: true },
    { key: 'description', label: 'Description', type: 'textarea', required: false },
  ],

  permission_grant: [
    { key: 'role', label: 'Role', type: 'token_ref', required: true, tokenRefSections: PERMISSION_ROLE_SECTIONS },
    { key: 'domain', label: 'Domain', type: 'select', required: true, options: PERMISSION_DOMAIN_OPTIONS },
    { key: 'node', label: 'Node', type: 'token_ref', required: true, tokenRefSections: GRANT_NODE_SECTIONS },
    { key: 'permission', label: 'Permission', type: 'select', required: true, options: PERMISSION_LEVEL_OPTIONS },
  ],

  permission_assignment: [
    { key: 'role', label: 'Role', type: 'token_ref', required: true, tokenRefSections: PERMISSION_ROLE_SECTIONS },
    { key: 'users', label: 'Users', type: 'token_ref_array', required: true, tokenRefSections: USER_TOKEN_SECTIONS },
  ],

  risk: [
    { key: 'token', label: 'Token', type: 'text', required: true, readOnly: true },
    { key: 'name', label: 'Name', type: 'text', required: true },
    { key: 'register', label: 'Register', type: 'token_ref', required: true, tokenRefSections: REGISTER_SECTIONS, lookupTypeFilter: REGISTER_LOOKUP_FILTER },
    { key: 'taxonomy', label: 'Taxonomy Node', type: 'token_ref', required: true, tokenRefSections: TAXONOMY_SECTIONS, lookupTypeFilter: TAXONOMY_LOOKUP_FILTER },
    { key: 'owner', label: 'Owner', type: 'token_ref', required: true, tokenRefSections: OWNERSHIP_ROLE_SECTIONS, lookupTypeFilter: OWNERSHIP_ROLE_LOOKUP_FILTER },
    { key: 'created_by', label: 'Created By', type: 'token_ref', required: true, tokenRefSections: USER_TOKEN_SECTIONS, lookupTypeFilter: USER_LOOKUP_FILTER },
    { key: 'event_description', label: 'Risk Event Description', type: 'textarea', required: false },
    { key: 'state', label: 'State', type: 'select', required: false, options: RISK_STATE_OPTIONS, placeholder: 'Default: draft' },
    { key: 'inherent', label: 'Inherent Score', type: 'scoring', required: false },
    { key: 'residual', label: 'Residual Score', type: 'scoring', required: false },
    { key: 'target', label: 'Target Score', type: 'scoring', required: false },
  ],

  control: [
    { key: 'token', label: 'Token', type: 'text', required: true, readOnly: true },
    { key: 'name', label: 'Name', type: 'text', required: true },
    { key: 'category', label: 'Category', type: 'token_ref', required: true, tokenRefSections: CONTROL_CATEGORY_SECTIONS, lookupTypeFilter: CONTROL_CATEGORY_LOOKUP_FILTER },
    { key: 'owner', label: 'Owner', type: 'token_ref', required: true, tokenRefSections: OWNERSHIP_ROLE_SECTIONS, lookupTypeFilter: OWNERSHIP_ROLE_LOOKUP_FILTER },
    { key: 'created_by', label: 'Created By', type: 'token_ref', required: true, tokenRefSections: USER_TOKEN_SECTIONS, lookupTypeFilter: USER_LOOKUP_FILTER },
    { key: 'description', label: 'Description', type: 'textarea', required: false },
    { key: 'state', label: 'State', type: 'select', required: false, options: CONTROL_STATE_OPTIONS, placeholder: 'Default: draft' },
    { key: 'risks', label: 'Linked Risks', type: 'token_ref_array', required: false, tokenRefSections: RISK_SECTIONS },
  ],

  event: [
    { key: 'token', label: 'Token', type: 'text', required: true, readOnly: true },
    { key: 'title', label: 'Title', type: 'text', required: true },
    { key: 'event_type', label: 'Event Type', type: 'token_ref', required: true, tokenRefSections: EVENT_TYPE_SECTIONS, lookupTypeFilter: EVENT_TYPE_LOOKUP_FILTER },
    { key: 'category', label: 'Category', type: 'token_ref', required: true, tokenRefSections: EVENT_CATEGORY_SECTIONS, lookupTypeFilter: EVENT_CATEGORY_LOOKUP_FILTER },
    { key: 'recorded_by', label: 'Recorded By', type: 'token_ref', required: true, tokenRefSections: USER_TOKEN_SECTIONS, lookupTypeFilter: USER_LOOKUP_FILTER },
    { key: 'description', label: 'Description', type: 'textarea', required: false },
    { key: 'event_date', label: 'Event Date', type: 'date', required: true, placeholder: 'YYYY-MM-DD' },
    { key: 'state', label: 'State', type: 'select', required: false, options: EVENT_STATE_OPTIONS, placeholder: 'Default: draft' },
    { key: 'risks', label: 'Linked Risks', type: 'token_ref_array', required: false, tokenRefSections: RISK_SECTIONS },
    { key: 'actions', label: 'Linked Actions', type: 'token_ref_array', required: false, tokenRefSections: ACTION_SECTIONS },
  ],

  action: [
    { key: 'token', label: 'Token', type: 'text', required: true, readOnly: true },
    { key: 'title', label: 'Title', type: 'text', required: true },
    { key: 'category', label: 'Category', type: 'token_ref', required: true, tokenRefSections: ACTION_CATEGORY_SECTIONS, lookupTypeFilter: ACTION_CATEGORY_LOOKUP_FILTER },
    { key: 'owner', label: 'Owner', type: 'token_ref', required: true, tokenRefSections: OWNERSHIP_ROLE_SECTIONS, lookupTypeFilter: OWNERSHIP_ROLE_LOOKUP_FILTER },
    { key: 'created_by', label: 'Created By', type: 'token_ref', required: true, tokenRefSections: USER_TOKEN_SECTIONS, lookupTypeFilter: USER_LOOKUP_FILTER },
    { key: 'description', label: 'Description', type: 'textarea', required: false },
    { key: 'due_date', label: 'Due Date', type: 'date', required: false, placeholder: 'YYYY-MM-DD' },
    { key: 'state', label: 'State', type: 'select', required: false, options: ACTION_STATE_OPTIONS, placeholder: 'Default: draft' },
    { key: 'risks', label: 'Linked Risks', type: 'token_ref_array', required: false, tokenRefSections: RISK_SECTIONS },
    { key: 'controls', label: 'Linked Controls', type: 'token_ref_array', required: false, tokenRefSections: CONTROL_SECTIONS },
  ],

  appetite: [
    { key: 'taxonomy', label: 'Taxonomy Node', type: 'token_ref', required: true, tokenRefSections: TAXONOMY_SECTIONS, lookupTypeFilter: TAXONOMY_LOOKUP_FILTER },
    { key: 'statement', label: 'Appetite Statement', type: 'textarea', required: false },
    { key: 'max_score', label: 'Max Score', type: 'number', required: true, placeholder: 'Maximum acceptable risk score' },
  ],

  assessment: [
    { key: 'control', label: 'Control', type: 'token_ref', required: true, tokenRefSections: CONTROL_SECTIONS },
    { key: 'type', label: 'Assessment Type', type: 'select', required: true, options: ASSESSMENT_TYPE_OPTIONS },
    { key: 'effectiveness', label: 'Effectiveness', type: 'select', required: true, options: EFFECTIVENESS_OPTIONS },
    { key: 'assessed_date', label: 'Assessed Date', type: 'date', required: true, placeholder: 'YYYY-MM-DD' },
    { key: 'assessed_by', label: 'Assessed By', type: 'token_ref', required: true, tokenRefSections: USER_TOKEN_SECTIONS, lookupTypeFilter: USER_LOOKUP_FILTER },
    { key: 'comment', label: 'Comment', type: 'textarea', required: false },
  ],

  update: [
    { key: 'action', label: 'Action', type: 'token_ref', required: true, tokenRefSections: ACTION_SECTIONS },
    { key: 'text', label: 'Update Text', type: 'textarea', required: true },
    { key: 'created_by', label: 'Created By', type: 'token_ref', required: true, tokenRefSections: USER_TOKEN_SECTIONS, lookupTypeFilter: USER_LOOKUP_FILTER },
  ],

  obligation_framework: [
    { key: 'token', label: 'Token', type: 'text', required: true, readOnly: true },
    { key: 'name', label: 'Name', type: 'text', required: true },
    { key: 'category', label: 'Category', type: 'token_ref', required: true, tokenRefSections: FRAMEWORK_CATEGORY_SECTIONS, lookupTypeFilter: FRAMEWORK_CATEGORY_LOOKUP_FILTER },
    // items and their mappings are nested arrays — handled specially by the editor
  ],
}

/**
 * Resolve the token_ref sections for a category field based on the section path.
 * Categories share the same schema but their 'parent' field references different sections.
 */
export function resolveCategoryParentSections(sectionPath: string): string[] {
  switch (sectionPath) {
    case 'structure/control_categories':
      return CONTROL_CATEGORY_SECTIONS
    case 'structure/event_categories':
      return EVENT_CATEGORY_SECTIONS
    case 'structure/action_categories':
      return ACTION_CATEGORY_SECTIONS
    case 'structure/framework_categories':
      return FRAMEWORK_CATEGORY_SECTIONS
    default:
      return []
  }
}

/**
 * Get the field schema for an item, resolving dynamic token_ref sections.
 * Returns a copy with resolved sections (does not mutate the original).
 */
export function getFieldSchemas(itemType: string, sectionPath: string): FieldSchema[] {
  const base = FIELD_SCHEMAS[itemType]
  if (!base) return []

  // Categories need dynamic parent section resolution
  if (itemType === 'category') {
    return base.map((field) => {
      if (field.key === 'parent') {
        return { ...field, tokenRefSections: resolveCategoryParentSections(sectionPath) }
      }
      return field
    })
  }

  return base
}

// --- Token collection helper ---

/**
 * Collect all tokens from the payload that belong to the specified section paths.
 * Used to populate token_ref dropdowns.
 *
 * Returns an array of { token, label } for display in select dropdowns.
 */
export interface TokenOption {
  token: string
  label: string
}

/**
 * Extract tokens and labels from a payload for given section paths.
 * Walks the payload structure using the same paths as SECTION_DEFS in importTree.ts.
 */
export function collectTokens(
  payload: Record<string, unknown>,
  sectionPaths: string[],
  lookupTypeFilter?: string[],
): TokenOption[] {
  const results: TokenOption[] = []
  const seen = new Set<string>()

  for (const path of sectionPaths) {
    const items = getItemsForSection(payload, path)
    for (const item of items) {
      const token = typeof item.token === 'string' ? item.token : ''
      if (!token || seen.has(token)) continue

      // When reading from 'lookups' and a type filter is set,
      // skip items whose 'type' doesn't match.
      if (path === 'lookups' && lookupTypeFilter && lookupTypeFilter.length > 0) {
        const itemType = typeof item.type === 'string' ? item.type : ''
        if (!lookupTypeFilter.includes(itemType)) continue
      }

      seen.add(token)
      const label = pickLabel(item)
      results.push({ token, label })
    }
  }

  return results
}

/** Section path → payload navigation map. Matches importTree.ts SECTION_DEFS. */
const SECTION_PAYLOAD_MAP: Record<string, string[]> = {
  'lookups': ['lookups'],
  'structure/users': ['structure', 'users'],
  'structure/ownership_roles': ['structure', 'ownership_roles'],
  'structure/registers': ['structure', 'registers'],
  'structure/control_categories': ['structure', 'control_categories'],
  'structure/event_categories': ['structure', 'event_categories'],
  'structure/action_categories': ['structure', 'action_categories'],
  'structure/framework_categories': ['structure', 'framework_categories'],
  'structure/event_types': ['structure', 'event_types'],
  'permissions/roles': ['permissions', 'roles'],
  'permissions/grants': ['permissions', 'grants'],
  'permissions/assignments': ['permissions', 'assignments'],
  'objects/risks': ['objects', 'risks'],
  'objects/controls': ['objects', 'controls'],
  'objects/events': ['objects', 'events'],
  'objects/actions': ['objects', 'actions'],
  'appetite': ['appetite'],
  'assessments': ['assessments'],
  'updates': ['updates'],
  'obligations': ['obligations'],
}

function getItemsForSection(
  payload: Record<string, unknown>,
  sectionPath: string,
): Record<string, unknown>[] {
  const keys = SECTION_PAYLOAD_MAP[sectionPath]
  if (!keys) return []

  let current: unknown = payload
  for (const key of keys) {
    if (current == null || typeof current !== 'object') return []
    current = (current as Record<string, unknown>)[key]
  }

  return Array.isArray(current)
    ? (current as Record<string, unknown>[])
    : []
}

function pickLabel(item: Record<string, unknown>): string {
  for (const field of ['name', 'title', 'display_name', 'email', 'token']) {
    const val = item[field]
    if (typeof val === 'string' && val) return val
  }
  return '(unnamed)'
}
